import React, { useEffect, useRef, useState } from 'react';
import { TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { fetchPlacePredictions, hasPlacesAutocomplete, PlacePrediction } from './google';

interface Props {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  displayValue?: string;
  displayMatchesValue?: boolean;
}

function makeAddressFieldId() {
  return `depart-address-${Math.random().toString(36).slice(2, 10)}`;
}

function cleanInputProps(inputProps: any) {
  const next = { ...inputProps };
  if (next['aria-activedescendant'] === '') {
    delete next['aria-activedescendant'];
  }
  return next;
}

export default function AddressField({
  label,
  value,
  onChange,
  disabled,
  displayValue,
  displayMatchesValue = false,
}: Props) {
  const idRef = useRef(makeAddressFieldId());
  const shownValue = displayMatchesValue && displayValue ? displayValue : value;
  const [input, setInput] = useState(shownValue);
  const [options, setOptions] = useState<PlacePrediction[]>([]);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    setInput(shownValue);
  }, [shownValue]);

  useEffect(() => {
    if (!hasPlacesAutocomplete()) return;
    if (!input || input === shownValue || input === displayValue) {
      setOptions([]);
      return;
    }
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      fetchPlacePredictions(input)
        .then(setOptions)
        .catch(() => setOptions([]));
    }, 250);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [displayValue, input, shownValue]);

  const commitInput = (nextInput: string) => {
    onChange(displayMatchesValue && nextInput === displayValue ? value : nextInput);
  };

  return (
    <Autocomplete
      id={idRef.current}
      freeSolo
      disabled={disabled}
      options={options.map(o => o.description)}
      filterOptions={x => x}
      inputValue={input}
      value={value}
      onInputChange={(_, v) => setInput(v)}
      onChange={(_, v) => commitInput((v as string) || '')}
      onBlur={() => commitInput(input)}
      renderInput={(params) => (
        <TextField
          {...params}
          inputProps={cleanInputProps(params.inputProps)}
          label={label}
          variant="outlined"
          size="small"
          fullWidth
        />
      )}
    />
  );
}
